import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle, GitCompare, Calculator, FileText, CalendarClock,
  Trash2, Inbox, Sparkles, Crown, MessageSquare, ArrowRight, Eye,
  Wallet, Target, Plane, Camera, UploadCloud, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/Markdown";
import { AiUsageCard } from "@/components/AiUsageCard";
import { ReminderDigest } from "@/components/ReminderDigest";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PLANS } from "@/lib/plans";

const TABS = [
  { type: "problem", tkey: "tab_problems", icon: AlertCircle },
  { type: "comparison", tkey: "tab_comparisons", icon: GitCompare },
  { type: "guide", tkey: "tab_guides", icon: FileText },
  { type: "calculation", tkey: "tab_calculations", icon: Calculator },
  { type: "plan", tkey: "tab_plans", icon: CalendarClock },
];

export default function Dashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tier = user?.membership_tier || "free";
  const isPaid = tier !== "free";
  const tierName = (PLANS[tier] || PLANS.free).name.en;
  const [active, setActive] = useState("problem");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [convos, setConvos] = useState([]);
  const [detail, setDetail] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [goals, setGoals] = useState([]);
  const [welcome, setWelcome] = useState(false);

  // One-time in-app welcome for members who upgraded (covers users who closed
  // the payment tab before the success popup showed). Keyed per tier in
  // localStorage so it appears exactly once per plan and never for Free.
  useEffect(() => {
    if (!isPaid) return;
    try {
      if (localStorage.getItem("evermin_welcomed") !== tier) {
        setWelcome(true);
        localStorage.setItem("evermin_welcomed", tier);
      }
    } catch (_) {}
  }, [isPaid, tier]);

  const loadAll = useCallback(() => {
    api.get("/saved-items").then(({ data }) => setAllItems(data)).catch(() => {});
  }, []);

  const loadSummary = useCallback(() => {
    api.get("/saved-items/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/ai/conversations").then(({ data }) => setConvos(data.slice(0, 3))).catch(() => setConvos([]));
    api.get("/files").then(({ data }) => setFiles((data || []).slice(0, 4))).catch(() => setFiles([]));
    api.get("/goals").then(({ data }) => setGoals(data || [])).catch(() => setGoals([]));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // My Life groupings — derived from real saved data only (no invented records).
  const isGoal = (i) => i.type === "calculation" && /savings goal|बचत लक्ष्य/i.test(i.title || "");
  const moneyItems = allItems.filter((i) => i.type === "calculation" && !isGoal(i));
  const goalItems = allItems.filter(isGoal);
  const planItems = allItems.filter((i) => i.type === "plan");

  const loadItems = useCallback((type) => {
    setLoading(true);
    api.get("/saved-items", { params: { type } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadItems(active); }, [active, loadItems]);

  const remove = async (id) => {
    try {
      await api.delete(`/saved-items/${id}`);
      setItems((prev) => prev.filter((i) => i.item_id !== id));
      setAllItems((prev) => prev.filter((i) => i.item_id !== id));
      toast.success(t("deleted"));
      loadSummary();
    } catch (e) {
      notifyError(e, t);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl" data-testid="dashboard-title">
              {t("my_life_title")}
            </h1>
            <p className="mt-1.5 text-slate-500">{t("my_life_subtitle")}</p>
          </div>
          <Badge
            variant="outline"
            className={`w-fit gap-1.5 rounded-full px-3 py-1.5 text-xs ${isPaid ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500"}`}
            data-testid="membership-badge"
          >
            {isPaid ? <Crown className="h-3.5 w-3.5" /> : null}
            {isPaid ? `EVERMIN ${tierName}` : t("free_tier") || "Free"}
          </Badge>
        </div>

        <ReminderDigest />
        <AiUsageCard />

        {tier !== "pro" && (
          <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:flex-row sm:items-center" data-testid="upgrade-prompt">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-400/30 text-amber-700"><Crown className="h-5 w-5" /></span>
              <div>
                <p className="font-display font-semibold text-slate-900">{isPaid ? t("upgrade_prompt_paid") : t("upgrade_prompt")}</p>
                <p className="text-sm text-slate-600">{t("upgrade_prompt_desc")}</p>
              </div>
            </div>
            <Button onClick={() => navigate("/membership")} data-testid="dashboard-upgrade-btn" className="gap-1.5 rounded-full bg-amber-400 font-semibold text-slate-900 hover:bg-amber-300 min-h-[44px]">
              <Sparkles className="h-4 w-4" /> {t("see_plans")}
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="font-display text-base font-bold text-slate-900">{t("ml_quick_actions")}</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { key: "qa_ask", icon: MessageSquare, to: "/chat" },
              { key: "qa_scan", icon: Camera, to: "/assistant" },
              { key: "qa_calc", icon: Calculator, to: "/calculators" },
              { key: "qa_pdf", icon: FileText, to: "/assistant" },
              { key: "qa_compare", icon: GitCompare, to: "/compare" },
              { key: "qa_reminders", icon: Bell, to: "/reminders" },
            ].map(({ key, icon: Icon, to }) => (
              <button key={key} onClick={() => navigate(to)} data-testid={`quick-${key}`}
                className="card-premium flex items-center gap-2.5 p-3.5 text-left transition-colors hover:border-primary/40">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-4.5 w-4.5 h-5 w-5" /></span>
                <span className="text-sm font-semibold text-slate-900">{t(key)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Goals & Plans (Phase 7) */}
        <div className="mt-8" data-testid="dashboard-goals">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> {t("goals_dash_title")}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/goals")} data-testid="dashboard-goals-viewall" className="gap-1 text-primary">
              {t("goals_dash_view")} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          {goals.length === 0 ? (
            <button onClick={() => navigate("/goals")} data-testid="dashboard-goals-empty"
              className="mt-3 flex w-full items-center justify-between rounded-2xl border border-dashed border-slate-300 bg-white/60 px-5 py-4 text-left hover:border-primary/40">
              <span className="text-sm text-slate-500">{t("goal_empty")}</span>
              <span className="flex items-center gap-1 text-sm font-semibold text-primary">{t("goal_new")} <ArrowRight className="h-3.5 w-3.5" /></span>
            </button>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {goals.filter((g) => g.status !== "archived").slice(0, 4).map((g) => (
                <button key={g.goal_id} onClick={() => navigate("/goals")} data-testid={`dashboard-goal-${g.goal_id}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-primary/40">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900">{g.title}</span>
                    <span className="flex-shrink-0 text-xs font-bold text-primary">{g.progress}%</span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${g.progress || 0}%` }} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* My Life collections — Money / Goals / Plans (real saved data only) */}
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <CollectionCard
            icon={Wallet} tone="text-emerald-600 bg-emerald-50" label={t("ml_money")} items={moneyItems}
            emptyText={t("ml_money_empty")} ctaLabel={t("qa_calc")} onCta={() => navigate("/calculators")}
            onOpen={setDetail} testid="ml-money" />
          <CollectionCard
            icon={Target} tone="text-indigo-600 bg-indigo-50" label={t("ml_goals")} items={goalItems}
            emptyText={t("ml_goals_empty")} ctaLabel={t("ml_set_goal")} onCta={() => navigate("/calculators/savings-goal")}
            onOpen={setDetail} testid="ml-goals" renderMeta={(i) => i.metadata?.results?.projected != null
              ? `${t("ml_projected")}: ₹${Number(i.metadata.results.projected).toLocaleString("en-IN")}` : null} />
          <CollectionCard
            icon={Plane} tone="text-amber-600 bg-amber-50" label={t("ml_plans")} items={planItems}
            emptyText={t("ml_plans_empty")} ctaLabel={t("qa_ask")} onCta={() => navigate("/chat")}
            onOpen={setDetail} testid="ml-plans" />
        </div>

        {/* Recent Activity */}
        {(convos.length > 0 || files.length > 0) && (
          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="recent-activity">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-bold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" /> {t("recent_conversations")}
                </h2>
                <Button asChild variant="ghost" size="sm" className="gap-1 text-primary" data-testid="view-all-chats-btn">
                  <a href="/chat">{t("new_chat")} <ArrowRight className="h-3.5 w-3.5" /></a>
                </Button>
              </div>
              {convos.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {convos.map((c) => (
                    <button key={c.conversation_id} onClick={() => navigate(`/chat/${c.conversation_id}`)} data-testid={`recent-conv-${c.conversation_id}`}
                      className="block w-full truncate rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm text-slate-600 hover:border-primary/40 hover:text-slate-900">
                      {c.title || t("new_chat")}
                    </button>
                  ))}
                </div>
              ) : <p className="mt-3 text-sm text-slate-400">{t("ml_no_activity")}</p>}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-bold text-slate-900 flex items-center gap-2">
                  <UploadCloud className="h-4 w-4 text-primary" /> {t("ml_uploads")}
                </h2>
                <Button asChild variant="ghost" size="sm" className="gap-1 text-primary" data-testid="upload-more-btn">
                  <a href="/assistant">{t("qa_scan")} <ArrowRight className="h-3.5 w-3.5" /></a>
                </Button>
              </div>
              {files.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {files.map((f) => (
                    <div key={f.file_id} data-testid={`recent-file-${f.file_id}`}
                      className="flex items-center gap-2 truncate rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                      <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
                      <span className="truncate">{f.original_filename || "file"}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="mt-3 text-sm text-slate-400">{t("ml_no_activity")}</p>}
            </div>
          </div>
        )}

        <h2 className="font-display mt-10 text-base font-bold text-slate-900">{t("ml_library")}</h2>
        <Tabs value={active} onValueChange={setActive} className="mt-4">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const count = summary[tab.type] || 0;
              return (
                <TabsTrigger
                  key={tab.type}
                  value={tab.type}
                  data-testid={`dashboard-tab-${tab.type}`}
                  className="min-h-[44px] gap-2 rounded-full border border-slate-200 bg-white px-4 data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t(tab.tkey)}</span>
                  <span className="sm:hidden">{t(tab.tkey).split(" ").pop()}</span>
                  {count > 0 && (
                    <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-xs text-slate-600 data-[state=active]:bg-white/20">{count}</span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TABS.map((tab) => (
            <TabsContent key={tab.type} value={tab.type} className="mt-6">
              {loading ? (
                <div className="space-y-3" data-testid="dashboard-loading">
                  {[0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}
                </div>
              ) : items.length === 0 ? (
                <EmptyState onCta={() => navigate("/")} />
              ) : (
                <div className="space-y-3" data-testid="saved-items-list">
                  {items.map((item) => (
                    <SavedItemCard key={item.item_id} item={item} onDelete={() => remove(item.item_id)} onOpen={() => setDetail(item)} />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>
      <Footer />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="saved-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{detail.title}</DialogTitle>
              </DialogHeader>
              <Markdown content={detail.content || ""} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* One-time in-app welcome for upgraded members */}
      <Dialog open={welcome} onOpenChange={setWelcome}>
        <DialogContent className="sm:max-w-md" data-testid="inapp-welcome-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-500">
              <Crown className="h-7 w-7" />
            </div>
            <DialogTitle className="font-display text-center text-2xl" data-testid="inapp-welcome-title">{t("welcome_title")}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-center text-slate-600" data-testid="inapp-welcome-thanks">{t("welcome_thanks")}</DialogDescription>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
            <p className="font-display text-base font-semibold text-slate-900">
              {t("welcome_ready").replace("{plan}", `EVERMIN ${tierName}`)}
            </p>
            <p className="mt-1.5 flex items-center justify-center gap-1.5 text-sm font-medium text-primary">
              <Sparkles className="h-4 w-4" /> {t("welcome_ai_limit").replace("{count}", (PLANS[tier] || PLANS.free).ai)}
            </p>
          </div>
          <Button onClick={() => setWelcome(false)} data-testid="inapp-welcome-cta-btn" className="w-full rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800">
            {t("welcome_cta")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ onCta }) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center" data-testid="dashboard-empty">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <Inbox className="h-7 w-7" />
      </span>
      <h3 className="font-display mt-4 text-lg font-semibold text-slate-900">{t("empty_title")}</h3>
      <p className="mt-1.5 max-w-sm text-sm text-slate-500">{t("empty_desc")}</p>
      <Button onClick={onCta} data-testid="empty-cta-btn" className="mt-5 gap-1.5 rounded-full min-h-[44px]">
        <Sparkles className="h-4 w-4" /> {t("empty_cta")}
      </Button>
    </div>
  );
}

function CollectionCard({ icon: Icon, tone, label, items, emptyText, ctaLabel, onCta, onOpen, renderMeta, testid }) {
  const recent = (items || []).slice(0, 3);
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></span>
        <h3 className="font-display font-semibold text-slate-900">{label}</h3>
        {items.length > 0 && <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{items.length}</span>}
      </div>
      {recent.length > 0 ? (
        <div className="mt-3 space-y-2">
          {recent.map((i) => (
            <button key={i.item_id} onClick={() => onOpen(i)} data-testid={`${testid}-item-${i.item_id}`}
              className="block w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left hover:border-primary/40">
              <span className="block truncate text-sm font-medium text-slate-800">{i.title}</span>
              {renderMeta && renderMeta(i) && <span className="mt-0.5 block truncate text-xs text-slate-500">{renderMeta(i)}</span>}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex flex-1 flex-col items-start justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-4">
          <p className="text-sm text-slate-500">{emptyText}</p>
          <button onClick={onCta} data-testid={`${testid}-cta`} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function SavedItemCard({ item, onDelete, onOpen }) {
  const { t } = useLanguage();
  const date = new Date(item.created_at);
  const dateStr = isNaN(date) ? "" : date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const preview = (item.content || "").replace(/[#*]/g, "").slice(0, 160);

  return (
    <div className="group rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-primary/30" data-testid="saved-item-card">
      <div className="flex items-start justify-between gap-4">
        <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid={`open-item-${item.item_id}`}>
          <h4 className="font-display font-semibold text-slate-900">{item.title}</h4>
          {preview && <p className="mt-1.5 text-sm leading-relaxed text-slate-500 line-clamp-2">{preview}…</p>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {dateStr && <span className="text-xs text-slate-400">{dateStr}</span>}
            {(item.tags || []).map((tag) => (
              <Badge key={tag} variant="secondary" className="rounded-full text-xs font-normal text-slate-500">{tag}</Badge>
            ))}
          </div>
        </button>
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onOpen} data-testid={`view-item-${item.item_id}`} aria-label={t("view_conversation")} className="h-9 w-9 text-slate-300 hover:bg-slate-100 hover:text-slate-600">
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} data-testid={`delete-item-${item.item_id}`} aria-label={t("delete")} className="h-9 w-9 text-slate-300 hover:bg-rose-50 hover:text-rose-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
